import type { TFunction } from 'i18next';

/** GERP 원가 견적서(121) — 레거시 appr_edit_forms_cost_buy_new */
export const COST_QUOTATION_TYPE = 121;

export const isCostQuotationType = (apvType: number): boolean =>
  apvType === COST_QUOTATION_TYPE;

export const COST_QUOTATION_MISSING_MESSAGE =
  '데이터를 로드하지 않았습니다. 조회 조건 선택 후 확인 버튼을 누르세요.';
export const COST_QUOTATION_PO_NOT_SENT_MESSAGE =
  '발주서 전송후 원가 견적서 추가가 가능합니다.';
export const COST_QUOTATION_PRE_COST_MISSING_MESSAGE =
  '예정원가 생성후 원가 견적서 추가가 가능합니다.';

/** 부분 반려 사유 상한 — 백엔드 검증과 같게 */
export const COST_QUOTATION_REJECT_REASON_MAX_LENGTH = 300;

type Value = string | number | null | undefined;

/** GERP 미상신 결재 건 — 키는 GERP 응답 그대로 */
export type CostQuotationCandidate = {
  sysId: string;
  aprvId: string;
  aprvTypcd: string;
  title?: string | null;
  docTit?: string | null;
  aprvRev?: Value;
  styleNo?: string | null;
};

export type CostQuotationPartcode = {
  aprvId?: string | null;
  partcode?: Value;
  styleNo?: Value;
};

export type CostQuotationCardHeader = {
  brand?: Value;
  season?: Value;
  partcode?: Value;
  styleNo?: Value;
  custName?: Value;
  qty?: Value;
  originName?: Value;
  preCostYn?: Value;
  poProgStsYn?: Value;
  person?: Value;
  personTel?: Value;
  currCd?: Value;
  currExch?: Value;
  imageName?: Value;
  imageUrl?: Value;
  cnYn?: Value;
  cnCnyExch?: Value;
  cnQuotAmt?: Value;
};

export type CostQuotationDetail = {
  typeCode?: Value;
  type1?: Value;
  currQuotAmt?: Value;
  quotAmt?: Value;
  negoAmt?: Value;
};

/** GERP 121 본문 카드 1장 — 키는 GERP 응답 그대로 */
export type CostQuotationCardData = {
  aprvId?: string | null;
  costQtaId?: string | null;
  header?: CostQuotationCardHeader | null;
  currency?: { curr?: Value; decimalPoint?: Value } | null;
  details?: CostQuotationDetail[] | null;
};

export type CostQuotationContent = {
  partcodes?: CostQuotationPartcode[] | null;
  cards?: CostQuotationCardData[] | null;
};

export type CostQuotationDraftSource = {
  code: number;
  attributes: Record<string, unknown>;
  display: {
    candidates: CostQuotationCandidate[] | null;
    selection: string[] | null;
    subject: string | null;
    content: CostQuotationContent | null;
  };
};

/** 작성 중 본문에 추가한 카드 */
export type CostQuotationCard = {
  aprvId: string;
  styleNo: string;
  /** 작성 시점 카드 HTML */
  html: string;
};

export type CostQuotationState = {
  systemId: string;
  approvalTypeCode: string;
  approvalIds: string[];
  /** 새로 작성할 때만. 저장 문서를 편집할 때는 비어 있고 content 만 */
  cards: CostQuotationCard[];
  /** 작성 시점 본문 HTML(카드 전체) */
  content: string | null;
};

export const text = (v: unknown): string =>
  v === null || v === undefined ? '' : String(v).trim();

export const parseCostQuotationAttributes = (
  attributes: Record<string, unknown> | null | undefined,
): CostQuotationState | null => {
  const systemId = text(attributes?.systemId);
  const approvalTypeCode = text(attributes?.approvalTypeCode);
  const approvalIds = text(attributes?.approvalId)
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);
  if (!systemId || !approvalTypeCode || !approvalIds.length) return null;
  return {
    systemId,
    approvalTypeCode,
    approvalIds,
    cards: [],
    content: text(attributes?.content) || null,
  };
};

export const parseCostQuotationContent = (
  attributes: Record<string, unknown> | null | undefined,
): string | null => text(attributes?.content) || null;

export const buildCostQuotationAttributes = (
  state: CostQuotationState | null | undefined,
) =>
  state?.approvalIds.length
    ? {
        systemId: state.systemId,
        approvalTypeCode: state.approvalTypeCode,
        approvalId: state.approvalIds.join(','),
        content: state.content,
      }
    : null;

/** 카드 목록이 바뀔 때마다 결재 건·본문을 다시 맞춤 */
export const withCostQuotationCards = (
  base: Pick<CostQuotationState, 'systemId' | 'approvalTypeCode'>,
  cards: CostQuotationCard[],
): CostQuotationState | null =>
  cards.length
    ? {
        ...base,
        approvalIds: cards.map((c) => c.aprvId),
        cards,
        content: cards.map((c) => c.html).join(''),
      }
    : null;

export const checkCostQuotationBeforeSave = (
  state: CostQuotationState | null | undefined,
): string | null =>
  state?.approvalIds.length && state.content
    ? null
    : COST_QUOTATION_MISSING_MESSAGE;

/** 레거시 체크 시 검사 순서 — 발주서 전송 → 예정원가 생성 */
export const checkCostQuotationCard = (
  card: CostQuotationCardData,
): string | null => {
  const h = card.header ?? {};
  if (text(h.poProgStsYn).toUpperCase() === 'N') {
    return COST_QUOTATION_PO_NOT_SENT_MESSAGE;
  }
  if (text(h.preCostYn).toUpperCase() === 'N') {
    return COST_QUOTATION_PRE_COST_MISSING_MESSAGE;
  }
  return null;
};

export const costQuotationKey = (
  state: CostQuotationState | null | undefined,
) => state?.approvalIds.join(',') ?? '';

/** 레거시 콤보 문구 — DOC_TIT */
export const buildCostQuotationCandidateLabel = (c: CostQuotationCandidate) =>
  c.docTit?.trim() || c.title?.trim() || c.aprvId;

/** 레거시 change_title_content — 고른 순서의 첫 품번 기준, 0건이면 빈 제목 */
export const buildCostQuotationSubject = (
  styleNos: string[],
  t: TFunction,
): string => {
  if (!styleNos.length) return '';
  if (styleNos.length === 1) {
    return t('원가견적서({{styleNo}})', { styleNo: styleNos[0] });
  }
  return t('원가견적서({{styleNo}}외 {{count}}건)', {
    styleNo: styleNos[0],
    count: styleNos.length - 1,
  });
};

/** 레거시는 체크할 때마다 본문 에디터를 이 문구로 덮어씀 */
export const buildCostQuotationEditorHtml = (
  styleNos: string[],
  t: TFunction,
): string =>
  `<p>${escapeHtml(
    t('추가품번 : {{styleNos}}', { styleNos: styleNos.join(',') }),
  )}</p>`;

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/** 제목·추가 품번 확인에 쓰는 품번 — 레거시 콤보 STYLE_NO */
export const getCostQuotationStyleNo = (
  card: CostQuotationCardData,
  partcodes: CostQuotationPartcode[] | null | undefined,
  candidate?: CostQuotationCandidate,
): string => {
  const partcode = partcodes?.find((p) => p.aprvId === card.aprvId);
  return (
    text(candidate?.styleNo) ||
    text(card.header?.styleNo) ||
    text(partcode?.styleNo) ||
    text(card.header?.partcode) ||
    text(partcode?.partcode)
  );
};

/** 카드 제목 품번 — 레거시 콤보 DOC_TIT */
export const getCostQuotationTitlePartcode = (
  card: CostQuotationCardData,
  candidate?: CostQuotationCandidate,
): string =>
  text(candidate?.docTit) ||
  text(card.header?.partcode) ||
  text(card.header?.styleNo);

const PLM_IMAGE_BASE_URL =
  'https://s3.ap-northeast-2.amazonaws.com/static-plm.fnf.co.kr/';

/** GERP 가 준 공개 주소 우선. 없으면 레거시처럼 PLM S3 주소 + 이미지 경로 */
export const resolveCostQuotationImageUrl = (
  header: CostQuotationCardHeader | null | undefined,
): string => {
  const url = text(header?.imageUrl);
  if (url) return url;
  const path = text(header?.imageName);
  if (!path) return '';
  if (/^(https?:)?\/\//i.test(path)) return path;
  return PLM_IMAGE_BASE_URL + path.replace(/^\/+/, '');
};

const toNumber = (v: Value): number => {
  const n = Number(text(v).replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
};

/** PHP round — 0.5 는 0 에서 먼 쪽으로. 음수 자리는 10·100 단위 */
const phpRound = (n: number, precision: number) => {
  const factor = 10 ** precision;
  const scaled = Math.abs(n) * factor;
  // 부동소수 오차(1.005 → 1.00499..) 보정
  const rounded = Math.round(Number(scaled.toPrecision(15)));
  return (Math.sign(n) * rounded) / factor;
};

/** PHP 5.6 number_format — 음수 자리수는 0 으로 취급 */
const numberFormat = (v: Value, decimals = 0) => {
  const d = Math.max(0, decimals);
  return phpRound(toNumber(v), d).toLocaleString('en-US', {
    minimumFractionDigits: d,
    maximumFractionDigits: d,
  });
};

export const COST_QUOTATION_SUPPLIER_PAY_CODE = '999';
export const COST_QUOTATION_MARKUP_CODE = '99999';

export type CostQuotationRow = {
  typeCode: string;
  label: string;
  currQuotAmt: string;
  quotAmt: string;
  negoAmt: string;
  /** 협력사 지급가·예상마크업 줄 배경 */
  highlighted: boolean;
  /** 예상마크업 금액 강조 */
  emphasized: boolean;
};

/**
 * 레거시 appr_sub_view_forms_buy_order_mstr_renew 금액 표시.
 * 견적(협력사·본사)은 KRW 면 10원 단위, 그 밖은 소수 2자리. 확정가(KRW)는 정수
 */
export const buildCostQuotationRows = (
  card: CostQuotationCardData,
): CostQuotationRow[] => {
  const curr = (
    text(card.header?.currCd) || text(card.currency?.curr)
  ).toUpperCase();
  return (card.details ?? []).map((d) => {
    const typeCode = text(d.typeCode);
    const label = text(d.type1);
    if (typeCode === COST_QUOTATION_MARKUP_CODE) {
      return {
        typeCode,
        label,
        currQuotAmt: '',
        quotAmt: '',
        negoAmt: text(d.negoAmt),
        highlighted: true,
        emphasized: true,
      };
    }
    const quot = (v: Value) =>
      curr === 'KRW'
        ? numberFormat(phpRound(toNumber(v), -1))
        : numberFormat(v, 2);
    const isSupplierPay = typeCode === COST_QUOTATION_SUPPLIER_PAY_CODE;
    const nego =
      isSupplierPay && (curr === 'KRW' || curr === 'USD')
        ? numberFormat(phpRound(toNumber(d.negoAmt), -1))
        : numberFormat(d.negoAmt);
    return {
      typeCode,
      label,
      currQuotAmt: quot(d.currQuotAmt),
      quotAmt: quot(d.quotAmt),
      negoAmt: nego,
      highlighted: isSupplierPay,
      emphasized: false,
    };
  });
};

export const formatCostQuotationNumber = numberFormat;
